// src/service/ErrorLoggingService.js
import { baseurl } from '../util/config';
import SessionManager from './SessionManager';

/**
 * Structured Error Logging Service
 * 
 * Provides structured logging with error categorization, severity levels,
 * and automatic backend submission for error tracking and analysis.
 */
class ErrorLoggingService {
  static LOG_LEVELS = {
    DEBUG: 'DEBUG',
    INFO: 'INFO',
    WARN: 'WARN',
    ERROR: 'ERROR',
    CRITICAL: 'CRITICAL'
  };

  static ERROR_CATEGORIES = {
    AUTHENTICATION: 'AUTHENTICATION',
    AUTHORIZATION: 'AUTHORIZATION',
    NETWORK: 'NETWORK',
    VALIDATION: 'VALIDATION',
    BUSINESS_LOGIC: 'BUSINESS_LOGIC',
    SYSTEM: 'SYSTEM',
    UNKNOWN: 'UNKNOWN'
  };

  // Backend endpoint for error logs (adjust based on your API structure)
  static LOG_ENDPOINT = baseurl + 'api/logs/error';

  /**
   * Log an error with structured information
   * @param {string} level - Log level (DEBUG, INFO, WARN, ERROR, CRITICAL)
   * @param {string} category - Error category
   * @param {string} message - Error message
   * @param {Error|Object} error - Error object or additional context
   * @param {Object} context - Additional context (userId, component, action, etc.)
   */
  static async log(level, category, message, error = null, context = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      error: error ? this._extractErrorInfo(error) : null,
      context: {
        ...context,
        userAgent: navigator.userAgent,
        url: window.location.href,
        path: window.location.pathname,
      },
      session: {
        hasSession: !!SessionManager.getAccessToken(),
        userId: this._getUserId(),
      }
    };

    // Always log to console for development
    this._logToConsole(level, logEntry);

    // Send to backend for production/structured logging
    if (level === this.LOG_LEVELS.ERROR || level === this.LOG_LEVELS.CRITICAL) {
      this._sendToBackend(logEntry).catch(err => {
        // Fallback: log to console if backend fails
        console.error('Failed to send error log to backend:', err);
      });
    }
  }

  /**
   * Log debug information
   */
  static debug(message, context = {}) {
    this.log(this.LOG_LEVELS.DEBUG, this.ERROR_CATEGORIES.SYSTEM, message, null, context);
  }

  /**
   * Log informational message
   */
  static info(message, context = {}) {
    this.log(this.LOG_LEVELS.INFO, this.ERROR_CATEGORIES.SYSTEM, message, null, context);
  }

  /**
   * Log warning
   */
  static warn(message, error = null, context = {}) {
    this.log(this.LOG_LEVELS.WARN, this.ERROR_CATEGORIES.SYSTEM, message, error, context);
  }

  /**
   * Log error
   */
  static error(message, error = null, context = {}) {
    const category = this._categorizeError(error);
    this.log(this.LOG_LEVELS.ERROR, category, message, error, context);
  }

  /**
   * Log critical error
   */
  static critical(message, error = null, context = {}) {
    const category = this._categorizeError(error);
    this.log(this.LOG_LEVELS.CRITICAL, category, message, error, context);
  }

  /**
   * Extract structured information from error object
   */
  static _extractErrorInfo(error) {
    if (!error) return null;

    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
        ...(error.code && { code: error.code }),
        ...(error.status && { status: error.status }),
        ...(error.statusText && { statusText: error.statusText }),
      };
    }

    if (typeof error === 'object') {
      return {
        ...error,
        stringified: JSON.stringify(error),
      };
    }

    return {
      message: String(error),
    };
  }

  /**
   * Categorize error based on error properties
   */
  static _categorizeError(error) {
    if (!error) return this.ERROR_CATEGORIES.UNKNOWN;

    // Network errors
    if (error.status || error.statusCode || error.message?.includes('fetch') || 
        error.message?.includes('network') || error.message?.includes('timeout')) {
      return this.ERROR_CATEGORIES.NETWORK;
    }

    // Authentication errors
    if (error.status === 401 || error.message?.includes('unauthorized') || 
        error.message?.includes('token') || error.message?.includes('login')) {
      return this.ERROR_CATEGORIES.AUTHENTICATION;
    }

    // Authorization errors
    if (error.status === 403 || error.message?.includes('forbidden') || 
        error.message?.includes('permission')) {
      return this.ERROR_CATEGORIES.AUTHORIZATION;
    }

    // Validation errors
    if (error.status === 400 || error.message?.includes('validation') || 
        error.message?.includes('invalid') || error.message?.includes('required')) {
      return this.ERROR_CATEGORIES.VALIDATION;
    }

    return this.ERROR_CATEGORIES.UNKNOWN;
  }

  /**
   * Get user ID from session or context
   */
  static _getUserId() {
    try {
      const token = SessionManager.getAccessToken();
      if (token) {
        // Try to decode JWT to get userId
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub || payload.userId || null;
      }
    } catch (e) {
      // Ignore decode errors
    }
    return null;
  }

  /**
   * Log to console with appropriate method
   */
  static _logToConsole(level, logEntry) {
    const logMessage = `[${level}] [${logEntry.category}] ${logEntry.message}`;
    
    switch (level) {
      case this.LOG_LEVELS.DEBUG:
        console.debug(logMessage, logEntry);
        break;
      case this.LOG_LEVELS.INFO:
        console.info(logMessage, logEntry);
        break;
      case this.LOG_LEVELS.WARN:
        console.warn(logMessage, logEntry);
        break;
      case this.LOG_LEVELS.ERROR:
      case this.LOG_LEVELS.CRITICAL:
        console.error(logMessage, logEntry);
        break;
      default:
        console.log(logMessage, logEntry);
    }
  }

  /**
   * Send log entry to backend
   */
  static async _sendToBackend(logEntry) {
    try {
      const token = SessionManager.getAccessToken();
      
      const response = await fetch(this.LOG_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify(logEntry),
      });

      if (!response.ok) {
        throw new Error(`Backend logging failed: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      // Don't throw - we don't want logging failures to break the app
      console.error('Error sending log to backend:', error);
      return null;
    }
  }
}

export default ErrorLoggingService;

